import type { FileImportRow } from "@/api/file_import"
import { format, parseISO } from "date-fns"

export function format_import_row_amount(amount: number | undefined): string {
  if (amount == null || Number.isNaN(amount)) {
    return "—"
  }
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount)
}

export function customer_label_for_import_row(row: FileImportRow, headers: string[]): string | undefined {
  const idx = headers.findIndex(h => {
    const t = h.trim().toLowerCase()
    return t === "source name" || t === "customer name"
  })
  if (idx < 0) {
    return undefined
  }
  const v = row.row_data?.[idx]?.trim()
  return v || undefined
}

export function truncate_import_text(text: string | undefined, max_len: number): string | undefined {
  if (!text?.trim()) {
    return undefined
  }
  const t = text.trim()
  return t.length > max_len ? `${t.slice(0, max_len)}…` : t
}

export interface ImportRowDisplayFields {
  invoice?: string
  date?: string
  kind?: string
  source?: string
  customer?: string
  /** Signed net amount used as deduction invoice amount on import. */
  amount?: string
  memo?: string
  import_group?: string
  linked_deduction_id?: string
}

export function import_row_display_fields(
  row: FileImportRow | undefined,
  headers: string[],
): ImportRowDisplayFields {
  if (!row) {
    return {}
  }
  const customer = customer_label_for_import_row(row, headers)
  const date =
    row.date != null && row.date !== ""
      ? format(parseISO(row.date), "MM/dd/yyyy")
      : undefined
  const import_group =
    row.bc_sot_import_group != null
      ? row.bc_sot_import_group_rows != null && row.bc_sot_import_group_rows > 1
        ? `#${row.bc_sot_import_group} (${row.bc_sot_import_group_rows} rows)`
        : `#${row.bc_sot_import_group}`
      : undefined

  return {
    invoice: row.invoice_number?.trim() || undefined,
    date,
    kind: row.kind ?? undefined,
    source: row.source ?? undefined,
    customer,
    amount: format_import_row_amount(row.net_amount),
    memo: truncate_import_text(row.memo, 120),
    import_group,
    linked_deduction_id: row.deduction_id,
  }
}

interface DetailItemProps {
  label: string
  value: string | undefined
  wide?: boolean
}

export function ImportRowDetailItem(props: DetailItemProps) {
  const { label, value, wide } = props
  if (!value) {
    return null
  }
  return (
    <div class={wide ? "col-span-2 sm:col-span-4 lg:col-span-6" : ""}>
      <dt class="text-[10px] uppercase tracking-wide text-gray-500">{label}</dt>
      <dd class="text-xs text-gray-900 break-words">{value}</dd>
    </div>
  )
}

interface ImportRowDetailGridProps {
  fields: ImportRowDisplayFields
}

export function ImportRowDetailGrid(props: ImportRowDetailGridProps) {
  const { fields } = props
  return (
    <dl class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-2 mt-1.5">
      <ImportRowDetailItem label="Invoice" value={fields.invoice} />
      <ImportRowDetailItem label="Date" value={fields.date} />
      <ImportRowDetailItem label="Kind" value={fields.kind} />
      <ImportRowDetailItem label="Source" value={fields.source} />
      <ImportRowDetailItem label="Customer" value={fields.customer} />
      <ImportRowDetailItem label="Amount" value={fields.amount} />
      <ImportRowDetailItem label="Import group" value={fields.import_group} />
      <ImportRowDetailItem label="Memo" value={fields.memo} wide={true} />
    </dl>
  )
}
